<template>
  <!-- Tableau modifiable d'une rubrique du catalogue du matériel (27/10/2026) : une ligne par article. -->
  <!-- « defile » : tableau plus large que la page, défilement horizontal (sinon en-têtes fixes au défilement vertical) -->
  <div ref="cadre" class="table-catalogue" :class="{ defile }">
    <!-- un seul article, présenté un réglage par ligne -->
    <!-- « enLigne » (03/11/2026) : réglages côte à côte sur une ligne (ex. prix des engins au-dessus de leur tableau) -->
    <table v-if="vertical" class="vertical" :class="{ 'en-ligne': enLigne }">
      <tbody>
        <tr v-for="c in colonnes" :key="c.cle">
          <th>{{ c.titre }}<span v-if="c.unite" class="unite-entete"> ({{ c.unite }})</span></th>
          <td :class="{ 'a-verifier': aVerifier(lignes[0], c.cle), modifiee: estChange(lignes[0], c.cle), 'cellule-vide': c.signalerVide && estVide(lignes[0][c.cle]),
            'cellule-erreur': !!erreur(c, lignes[0], c.cle) }" :title="erreur(c, lignes[0], c.cle) || null">
            <template v-if="c.type === 'nombre'">
              <input type="text" inputmode="decimal" class="nombre" :value="valeurCase(c, lignes[0], c.cle)" :data-cle="c.cle" :disabled="lecture"
                :placeholder="c.signalerVide ? 'non chiffré' : ''"
                @focus="entrer(c, lignes[0], c.cle)" @blur="sortir" @input="saisir(c, lignes[0], c.cle, $event.target.value)" />
            </template>
            <input v-else type="text" :value="lignes[0][c.cle]" :data-cle="c.cle" :disabled="lecture" @input="ecrire(lignes[0], c.cle, $event.target.value)" />
            <button v-if="aVerifier(lignes[0], c.cle) && !lecture" type="button" class="btn-valider" aria-label="Valider cette valeur" @click="valider(lignes[0], c.cle)">✓</button>
          </td>
        </tr>
      </tbody>
    </table>
    <table v-else>
      <thead>
        <!-- en-tête sur deux lignes (03/11/2026) : colonnes regroupées (« groupe » : Agricole, ICT…) -->
        <tr v-if="groupes">
          <template v-for="(g, gi) in groupes">
            <th v-if="g.groupe" :key="'g' + gi" :colspan="g.colonnes.length" class="entete-groupe" :class="{ 'colonne-liee': g.colonnes[0].lie }">{{ g.groupe }}</th>
            <th v-else v-for="c in g.colonnes" :key="c.cle" rowspan="2" :class="classeEntete(c)" :style="c.style && c.style.width ? { width: c.style.width } : null"
              :aria-sort="ariaTri(c)" @click="trier(c)">{{ c.titre }}<span v-if="c.unite" class="unite-entete"> ({{ c.unite }})</span><span v-if="tri.cle === c.cle" class="fleche">{{ tri.sens > 0 ? '▲' : '▼' }}</span></th>
          </template>
          <th v-if="!fixe && !lecture" class="col-action" rowspan="2"></th>
        </tr>
        <tr>
          <!-- tri par colonne (30/10/2026) : croissant, décroissant, ordre d'origine -->
          <th v-for="c in (groupes ? colonnes.filter(x => x.groupe) : colonnes)" :key="c.cle" :class="classeEntete(c)"
            :style="c.style && c.style.width ? { width: c.style.width } : null" :aria-sort="ariaTri(c)" @click="trier(c)">
            {{ c.titre }}<span v-if="c.unite" class="unite-entete"> ({{ c.unite }})</span><span v-if="tri.cle === c.cle" class="fleche">{{ tri.sens > 0 ? '▲' : '▼' }}</span>
          </th>
          <th v-if="!fixe && !groupes && !lecture" class="col-action"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(ligne, i) in affichees" :key="i" :data-ligne="i" :class="{ 'ligne-masquee': ligne.visible === false }">
          <td v-for="c in colonnesDe(ligne)" :key="c.cle" :class="{ 'cellule-vide': c.signalerVide && estVide(ligne[cleDe(c, ligne)]), 'a-verifier': aVerifier(ligne, cleDe(c, ligne)),
            modifiee: c.type !== 'calcule' && c.type !== 'lecture' && estChange(ligne, cleDe(c, ligne)), 'colonne-liee': c.lie,
            'cellule-inactive': c.inactif && c.inactif(ligne), 'col-oeil': c.type === 'visible', 'col-case': c.type === 'case' || c.type === 'caseListe', 'cellule-erreur': !!erreur(c, ligne, cleDe(c, ligne)) }"
            :title="erreur(c, ligne, cleDe(c, ligne)) || null">
            <span v-if="c.type === 'lecture'" class="lecture">{{ libelleOption(c, ligne[c.cle]) }}</span>
            <!-- matériel proposé dans les études (03/11/2026) : œil cliquable, ligne grisée quand elle est masquée -->
            <button v-else-if="c.type === 'visible'" type="button" class="btn-oeil" :data-cle="c.cle" :disabled="lecture"
              :aria-pressed="ligne[c.cle] !== false ? 'true' : 'false'"
              :title="ligne[c.cle] !== false ? 'Proposé dans les études : cliquer pour le masquer' : 'Masqué dans les études : cliquer pour le proposer'"
              @click="ecrire(ligne, c.cle, ligne[c.cle] === false)">
              <svg v-if="ligne[c.cle] !== false" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="currentColor" d="M12 5C7 5 2.7 8.1 1 12c1.7 3.9 6 7 11 7s9.3-3.1 11-7c-1.7-3.9-6-7-11-7zm0 11.5A4.5 4.5 0 1 1 12 7.5a4.5 4.5 0 0 1 0 9zm0-7a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5z"/></svg>
              <svg v-else viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path fill="currentColor" d="M2.1 3.5 3.5 2.1l18.4 18.4-1.4 1.4-3.2-3.2A11.6 11.6 0 0 1 12 19C7 19 2.7 15.9 1 12a12.4 12.4 0 0 1 4.3-5.2L2.1 3.5zM12 7.5a4.5 4.5 0 0 1 4.3 5.9l3.3 3.3A12 12 0 0 0 23 12c-1.7-3.9-6-7-11-7-1.3 0-2.6.2-3.8.6l2 2A4.5 4.5 0 0 1 12 7.5zm-4.4 3.5a4.5 4.5 0 0 0 5.4 5.4L7.6 11z"/></svg>
            </button>
            <span v-else-if="c.type === 'calcule'" class="calcule">{{ calcule(c, ligne) }}<span v-if="c.renvoi && c.renvoi(ligne)" class="renvoi">{{ c.renvoi(ligne) }}</span></span>
            <span v-else-if="(c.siPresent || c.cles) && !(cleDe(c, ligne) in ligne)" class="sans-objet">—</span>
            <select v-else-if="c.type === 'fabricant'" :value="ligne[c.cle]" :data-cle="c.cle" class="fabricant" :disabled="bloque(c, ligne)"
              @change="choisirFabricant(ligne, c.cle, $event)">
              <option v-for="m in fabricants(c.cle, ligne[c.cle])" :key="m" :value="m">{{ m || '—' }}</option>
              <option value="__nouveau">+ Nouveau fabricant…</option>
            </select>
            <input v-else-if="c.type === 'case'" type="checkbox" :checked="!!ligne[c.cle]" :data-cle="c.cle" :disabled="bloque(c, ligne)"
              @change="ecrire(ligne, c.cle, $event.target.checked)" />
            <!-- une valeur d'une liste (typologies de toiture, 03/11/2026) : une colonne par valeur -->
            <input v-else-if="c.type === 'caseListe'" type="checkbox" :checked="(ligne[c.champ] || []).includes(c.valeur)" :data-cle="c.cle"
              :disabled="bloque(c, ligne)" @change="basculer(ligne, c.champ, c.valeur, $event.target.checked, c.ordre)" />
            <!-- plusieurs choix (typologies d'un système d'intégration, 03/11/2026) -->
            <span v-else-if="c.type === 'multi'" class="multi" :data-cle="c.cle">
              <label v-for="o in c.options" :key="o.valeur" class="multi-option">
                <input type="checkbox" :checked="(ligne[c.cle] || []).includes(o.valeur)" :disabled="bloque(c, ligne)" @change="basculer(ligne, c.cle, o.valeur, $event.target.checked, c.options)" />{{ o.libelle }}
              </label>
            </span>
            <select v-else-if="c.type === 'choix'" :value="ligne[c.cle]" :data-cle="c.cle" :style="c.style" :disabled="bloque(c, ligne)"
              @change="ecrire(ligne, c.cle, $event.target.value)">
              <option v-for="o in c.options" :key="o.valeur" :value="o.valeur">{{ o.libelle }}</option>
            </select>
            <template v-else-if="c.type === 'nombre'">
              <input type="text" inputmode="decimal" class="nombre" :value="valeurCase(c, ligne, cleDe(c, ligne))" :data-cle="cleDe(c, ligne)" :style="c.style" :disabled="bloque(c, ligne)"
                :placeholder="c.signalerVide ? 'non chiffré' : ''"
                @focus="entrer(c, ligne, cleDe(c, ligne))" @blur="sortir" @input="saisir(c, ligne, cleDe(c, ligne), $event.target.value)" />
            </template>
            <input v-else type="text" :value="ligne[c.cle]" :data-cle="c.cle" :style="[c.largeur ? { width: c.largeur } : null, c.style]" :disabled="bloque(c, ligne)"
              @change="ecrire(ligne, c.cle, $event.target.value)" />
            <button v-if="aVerifier(ligne, cleDe(c, ligne)) && !lecture" type="button" class="btn-valider" aria-label="Valider cette valeur" @click="valider(ligne, cleDe(c, ligne))">✓</button>
          </td>
          <td v-if="!fixe && !lecture" class="col-action">
            <!-- poubelle (03/11/2026) -->
            <button type="button" class="btn-supprimer" aria-label="Supprimer cette ligne" title="Supprimer cette ligne" @click="supprimer(ligne)">
              <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path fill="currentColor" d="M9 3h6l1 1h4v2H4V4h4l1-1zm-3 5h12l-1 13H7L6 8zm4 2v9h1.5v-9H10zm3.5 0v9H15v-9h-1.5z"/></svg>
            </button>
          </td>
        </tr>
      </tbody>
    </table>
    <button v-if="!fixe && !vertical && !lecture" type="button" class="btn-ajouter" @click="ajouter">+ Ajouter</button>
  </div>
</template>

<script>
export default {
  name: 'TableCatalogue',
  props: {
    // lignes du catalogue, modifiées sur place (le parent compare le catalogue à sa version enregistrée)
    lignes: { type: Array, required: true },
    // [{ cle, cles, titre, type: 'texte' | 'nombre' | 'case' | 'choix' | 'fabricant' | 'lecture' | 'calcule' (valeur, decimales), unite,
    //    options: [{ valeur, libelle }], signalerVide, siPresent (« — » si la ligne n'a pas ce champ), largeur,
    //    renvoi (type calcule : marque d'un avertissement écrit sous le tableau, ex. « * »),
    //    parLigne (ligne => réglages propres à la ligne, ex. { type: 'case' }), style (de la case : largeur, police),
    //    echelle (nombre affiché = valeur × echelle, ex. 100 pour un prix enregistré en €/Wc saisi en c€/Wc) }]
    colonnes: { type: Array, required: true },
    // ligne ajoutée par « + Ajouter » (copie)
    modele: { type: Object, default: () => ({}) },
    // nombre de lignes fixe (un seul article, ex. optimiseur) : ni ajout ni suppression
    fixe: { type: Boolean, default: false },
    // un seul article (lignes[0]) présenté un réglage par ligne
    vertical: { type: Boolean, default: false },
    // lignes affichées (ex. onduleurs hybrides) ; ajout et suppression dans la liste complète
    filtre: { type: Function, default: null },
    // lecture seule (03/11/2026) : catalogue consultable par tous, modifiable par les administrateurs
    lecture: { type: Boolean, default: false },
    enLigne: { type: Boolean, default: false },
  },
  data() {
    // enEdition : case en cours de saisie (valeur brute, sans mise en forme) ; defile : tableau plus large que la page
    return { tri: { cle: null, sens: 1 }, enEdition: null, saisie: '', defile: false }
  },
  mounted() {
    this.mesurer()
    window.addEventListener('resize', this.mesurer)
  },
  updated() { this.mesurer() },
  beforeDestroy() { window.removeEventListener('resize', this.mesurer) },
  computed: {
    // colonnes consécutives d'un même « groupe » (Agricole, ICT…) : en-tête sur deux lignes ; null sans groupe
    groupes() {
      if (!this.colonnes.some(c => c.groupe)) return null
      const g = []
      this.colonnes.forEach(c => {
        const dernier = g[g.length - 1]
        if (c.groupe && dernier && dernier.groupe === c.groupe) dernier.colonnes.push(c)
        else g.push({ groupe: c.groupe || null, colonnes: [c] })
      })
      return g
    },
    visibles() {
      return this.filtre ? this.lignes.filter(this.filtre) : this.lignes
    },
    // lignes dans l'ordre du tri choisi (affichage seulement : l'ordre du catalogue ne change pas)
    affichees() {
      const c = this.tri.cle && this.colonnes.find(x => x.cle === this.tri.cle)
      if (!c) return this.visibles
      const sens = this.tri.sens
      return this.visibles.slice().sort((a, b) => {
        const va = this.valeurTri(c, a), vb = this.valeurTri(c, b)
        const videA = va === null || va === '', videB = vb === null || vb === ''
        if (videA || videB) return videA === videB ? 0 : videA ? 1 : -1 // cases vides à la fin
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * sens
        return String(va).localeCompare(String(vb), 'fr', { numeric: true, sensitivity: 'base' }) * sens
      })
    },
  },
  methods: {
    /**
     * Saisie invalide (03/11/2026), signalée dans la case : valeur négative, au-delà du maximum de la colonne (marges :
     * moins de 100 %), valeur obligatoire manquante (« obligatoire » : puissance d'un panneau ou d'un onduleur).
     */
    erreur(c, ligne, cle) {
      if (!ligne || c.type !== 'nombre' || c.derive) return ''
      const v = ligne[cle]
      if (this.estVide(v)) return c.obligatoire ? 'Valeur obligatoire' : ''
      if (v < 0) return 'Valeur négative'
      if (c.max != null && v > c.max) return c.messageMax || 'Au plus ' + c.max
      if (c.obligatoire && v <= 0) return 'Valeur obligatoire'
      return ''
    },
    libelleOption(c, v) {
      const o = (c.options || []).find(x => x.valeur === v)
      return o ? o.libelle : v
    },
    classeEntete(c) {
      return { 'entete-court': c.style && c.style.width, 'col-case': c.type === 'case' || c.type === 'caseListe',
        'col-nombre': c.type === 'nombre' || c.type === 'calcule' || c.type === 'case' || c.type === 'caseListe' || c.type === 'visible', triable: this.lignes.length > 1 && c.type !== 'visible', trie: this.tri.cle === c.cle,
        'colonne-liee': c.lie, 'col-oeil': c.type === 'visible' }
    },
    // case non modifiable : lecture seule, ou sans objet pour la ligne (ex. panneaux du carport d'un système qui n'en est pas un)
    bloque(c, ligne) { return this.lecture || !!(c.inactif && c.inactif(ligne)) },
    // colonnes d'une ligne : réglages propres à la ligne (« parLigne ») ajoutés à ceux de la colonne
    colonnesDe(ligne) {
      return this.colonnes.map(c => (c.parLigne ? { ...c, ...c.parLigne(ligne) } : c))
    },
    // colonne commune à des champs différents selon la ligne (« cles » : le premier présent)
    cleDe(c, ligne) {
      return c.cles ? c.cles.find(k => k in ligne) : c.cle
    },
    // fabricants de la section (29/10/2026), plus la valeur de la ligne
    fabricants(cle, actuel) {
      const l = [...new Set(this.lignes.map(x => x[cle]).filter(x => x))].sort()
      if (!l.includes(actuel)) l.unshift(actuel || '')
      return l
    },
    choisirFabricant(ligne, cle, e) {
      if (e.target.value !== '__nouveau') { this.ecrire(ligne, cle, e.target.value); return }
      const nom = (window.prompt('Nom du nouveau fabricant') || '').trim().toUpperCase()
      if (nom) this.ecrire(ligne, cle, nom)
      else e.target.value = ligne[cle] || ''
    },
    ecrire(ligne, cle, valeur) {
      this.$set(ligne, cle, valeur)
      this.valider(ligne, cle) // une valeur saisie est vérifiée
    },
    // valeurs temporaires ou douteuses (« aVerifier » de la ligne, 29/10/2026) : case en couleur jusqu'à sa validation
    aVerifier(ligne, cle) {
      return !!ligne && Array.isArray(ligne.aVerifier) && ligne.aVerifier.includes(cle)
    },
    valider(ligne, cle) {
      if (this.aVerifier(ligne, cle)) {
        const reste = ligne.aVerifier.filter(k => k !== cle)
        if (reste.length) this.$set(ligne, 'aVerifier', reste)
        else this.$delete(ligne, 'aVerifier')
      }
      this.$emit('modifie')
    },
    estVide(v) { return v === null || v === undefined || v === '' },
    mesurer() {
      const cadre = this.$refs.cadre
      const table = cadre && cadre.querySelector('table')
      const defile = !!table && table.scrollWidth > cadre.clientWidth + 1
      if (defile !== this.defile) this.defile = defile
    },
    // nombres à la française dans les cases (« 1 345,5 »), valeur brute pendant la saisie (30/10/2026)
    valeurCase(c, ligne, cle) {
      if (this.enEdition && this.enEdition.ligne === ligne && this.enEdition.cle === cle) return this.saisie
      const v = this.afficher(c, c.derive ? c.derive.get(ligne) : ligne[cle])
      return this.estVide(v) ? '' : Number(v).toLocaleString('fr-FR', { maximumFractionDigits: 4 }).replace(/\u202f/g, '\u00a0')
    },
    entrer(c, ligne, cle) {
      const v = this.afficher(c, c.derive ? c.derive.get(ligne) : ligne[cle])
      this.saisie = this.estVide(v) ? '' : String(v).replace('.', ',')
      this.enEdition = { ligne, cle }
    },
    sortir() { this.enEdition = null },
    saisir(c, ligne, cle, texte) {
      this.saisie = texte
      const propre = texte.replace(/[\s\u00a0\u202f]/g, '').replace(',', '.')
      // case liée à une autre (ratio au Wc ↔ prix d'un panneau, 03/11/2026) : la saisie modifie le champ enregistré
      if (c.derive) {
        if (propre === '' || !isNaN(Number(propre))) { c.derive.set(ligne, propre === '' ? null : this.lire(c, propre), this.$set); this.valider(ligne, c.derive.cle || cle) }
        return
      }
      if (propre === '') { this.ecrire(ligne, cle, null); return }
      if (!isNaN(Number(propre))) this.ecrire(ligne, cle, this.lire(c, propre))
    },
    basculer(ligne, cle, valeur, coche, options) {
      const actuel = ligne[cle] || []
      const l = coche ? actuel.concat([valeur]) : actuel.filter(v => v !== valeur)
      const ordre = options ? options.map(o => (typeof o === 'string' ? o : o.valeur)) : l
      this.ecrire(ligne, cle, ordre.filter(v => l.includes(v)).concat(l.filter(v => !ordre.includes(v))))
    },
    // valeur changée depuis le chargement (« __orig » posé par la page, non enregistré) ; ligne ajoutée : tout est nouveau
    estChange(ligne, cle) {
      if (!ligne || cle === undefined) return false
      const o = ligne.__orig
      if (!o) return true
      return JSON.stringify(o[cle] === undefined ? null : o[cle]) !== JSON.stringify(ligne[cle] === undefined ? null : ligne[cle])
    },
    trier(c) {
      if (this.lignes.length < 2 || c.type === 'visible') return
      if (this.tri.cle !== c.cle) this.tri = { cle: c.cle, sens: 1 }
      else if (this.tri.sens > 0) this.tri = { cle: c.cle, sens: -1 }
      else this.tri = { cle: null, sens: 1 }
    },
    ariaTri(c) {
      return this.tri.cle !== c.cle ? 'none' : this.tri.sens > 0 ? 'ascending' : 'descending'
    },
    valeurTri(c, ligne) {
      let v
      if (c.type === 'calcule') v = c.valeur(ligne)
      else if (c.derive) v = c.derive.get(ligne)
      else v = ligne[this.cleDe(c, ligne)]
      if (v === undefined || v === null || v === '' || (typeof v === 'number' && !isFinite(v))) return null
      if (typeof v === 'boolean') return v ? 1 : 0
      if (c.options) { const o = c.options.find(x => x.valeur === v); if (o) return o.libelle }
      return v
    },
    // valeur affichée et saisie à l'échelle de la colonne (c€/Wc pour un prix enregistré en €/Wc, 30/10/2026)
    afficher(c, v) {
      if (!c.echelle || this.estVide(v)) return v
      return Math.round(Number(v) * c.echelle * 1e6) / 1e6
    },
    lire(c, texte) {
      if (texte === '') return null
      return c.echelle ? Number(texte) / c.echelle : Number(texte)
    },
    // valeur calculée, pour information (ex. prix au Wc d'un panneau)
    calcule(c, ligne) {
      const v = c.valeur(ligne)
      return v === null || v === undefined || !isFinite(v) ? '—'
        : v.toLocaleString('fr-FR', { minimumFractionDigits: c.decimales || 0, maximumFractionDigits: c.decimales || 0 })
    },
    ajouter() {
      const ligne = JSON.parse(JSON.stringify(this.modele))
      delete ligne.aVerifier
      this.lignes.push(ligne)
      this.$emit('modifie')
    },
    supprimer(ligne) {
      this.lignes.splice(this.lignes.indexOf(ligne), 1)
      this.$emit('modifie')
    },
  },
}
</script>

<style scoped>
.table-catalogue { margin-bottom: 8px; }
.table-catalogue.defile { overflow-x: auto; }
/* en-têtes visibles au défilement de la page (tableau qui tient dans la largeur) */
.table-catalogue:not(.defile) thead th { position: sticky; top: 0; z-index: 1; background-color: #fff; }
tbody tr:nth-child(even) td { background: #fafbfc; }
tbody tr:hover td { background: #f3f6fb; }
.unite-entete { font-weight: 400; color: #8a909c; font-size: 11px; white-space: nowrap; }
td.cellule-erreur input { border-color: #c62828 !important; background: #fdecea !important; }
.unite-case { margin-left: 4px; font-size: 11px; color: #8a909c; white-space: nowrap; }

/* valeur changée depuis le dernier enregistrement */
.modifiee input, .modifiee select { border-color: #1e88e5; box-shadow: 0 0 0 1px #1e88e5 inset; }
/* fabricant en pastille */
td select.fabricant { border-color: transparent; background-color: #eef2f8; border-radius: 12px; font-weight: 600; color: #2b3a55; padding: 3px 8px; }
td select.fabricant:hover, td select.fabricant:focus { border-color: #b8c4d8; }
/* largeur selon le contenu, pas toute la page (29/10/2026 : cases trop larges, catalogue difficile à lire) */
table { border-collapse: collapse; width: auto; font-size: 13px; }
/* en-têtes centrés, colonnes au plus juste (03/11/2026) ; une colonne sans sous-titre occupe les deux lignes */
/* retour à la ligne automatique des titres (comme « fusionner et centrer » d'Excel) : colonne de nombres au plus juste */
th { text-align: center; vertical-align: middle; font-weight: 600; color: #555; padding: 5px 4px; white-space: normal; line-height: 1.25;
  border-bottom: none; background-image: linear-gradient(#e2e5ea, #e2e5ea); background-size: 100% 2px; background-position: bottom; background-repeat: no-repeat; }
th.col-nombre { max-width: 84px; }
/* colonne étroite : unité sur sa propre ligne, sous le titre (« (c€/Wc) » était coupé, 03/11/2026) */
th.col-nombre .unite-entete { display: block; white-space: nowrap; }
/* filet sous un titre de groupe, dessiné en fond : un tableau aux en-têtes fixes au défilement perd ses bordures */
thead tr:first-child th.entete-groupe { background-size: calc(100% - 12px) 1px; background-image: linear-gradient(#c9d0db, #c9d0db); }
th.entete-groupe { white-space: nowrap; }
td.col-case, th.col-case { text-align: center; }
th .unite { font-weight: 400; color: #888; }
th.triable { cursor: pointer; user-select: none; }
th.triable:hover { color: #1a1a2e; }
th.trie { color: #1a1a2e; }
.fleche { margin-left: 4px; font-size: 10px; color: #1565c0; }
/* colonne étroite (style.width) : titre sur deux lignes */
th.entete-court { white-space: normal; }
td { padding: 2px 3px; border-bottom: 1px solid #f0f1f4; }
td input[type=text], td input[type=number], td select { padding: 4px 6px; border: 1px solid #d5d9e0; border-radius: 4px; font-size: 13px; }
td input[type=text] { width: 200px; }
/* après la règle des cases de texte : même spécificité ; nombres au plus juste (03/11/2026) */
td input.nombre[type=text] { width: 68px; text-align: right; }
/* « non chiffré » dans une case vide étroite : en entier */
td input.nombre[type=text]::placeholder { font-size: 10.5px; }
table.en-ligne tbody { display: flex; flex-wrap: wrap; gap: 6px 18px; }
table.en-ligne tr { display: flex; align-items: center; gap: 6px; }
table.en-ligne th { border: none; padding: 0; font-weight: 600; }
table.en-ligne td { border: none; padding: 0; background: none !important; }
td { white-space: nowrap; }
table.vertical td input.nombre { width: 110px; }

table.vertical { width: auto; }
table.vertical th { border-bottom: 1px solid #f0f1f4; padding-right: 16px; font-weight: 600; }
table.vertical td input { width: 140px; }
.lecture { font-weight: 600; color: #3a3f4b; }
.calcule { display: inline-block; min-width: 60px; padding: 4px 6px; color: #666; text-align: right; }
.renvoi { margin-left: 2px; color: #b26a00; font-weight: 700; }
.sans-objet { color: #b0b6c0; padding-left: 8px; }
.cellule-vide input { background: #fff3e0; border-color: #f0a040; }
/* valeur temporaire ou douteuse, à vérifier */
.a-verifier { white-space: nowrap; }
.a-verifier input, .a-verifier select { background: #fff6bf; border-color: #d9a400; }
.btn-valider { margin-left: 3px; padding: 1px 6px; border: 1px solid #d9a400; border-radius: 10px; background: #fff; color: #8a6a00; font-size: 12px; cursor: pointer; }
.col-action { width: 32px; text-align: center; }
.btn-supprimer { border: none; background: none; color: #c62828; cursor: pointer; font-size: 14px; }
.btn-ajouter { margin-top: 6px; padding: 4px 12px; border: 1px dashed #b0b6c0; border-radius: 14px; background: #fff; color: #3a3f4b; font-size: 13px; cursor: pointer; }
.ligne-masquee td { opacity: .45; background: #f1f1f1 !important; }
.ligne-masquee td.col-oeil { opacity: 1; }
.col-oeil { width: 34px; text-align: center; }
.btn-oeil { border: none; background: none; cursor: pointer; color: #1565c0; padding: 2px; line-height: 0; }
.ligne-masquee .btn-oeil { color: #9e9e9e; }
.btn-oeil:disabled { cursor: default; }
th.entete-groupe { text-align: center; font-weight: 700; }
/* colonnes liées (prix d'achat ↔ ratio, 03/11/2026) : même fond, encadrées */
th.colonne-liee, td.colonne-liee { background-color: #eaf3fd !important; }
th.colonne-liee { color: #1565c0; }
td.cellule-inactive input, td.cellule-inactive select { background: #eee; color: #aaa; border-color: #e0e0e0; }
td.cellule-inactive { opacity: .6; }
input:disabled, select:disabled { cursor: default; }
.multi { display: flex; flex-wrap: wrap; gap: 2px 8px; font-size: 12px; }
.multi-option { display: inline-flex; align-items: center; gap: 3px; white-space: nowrap; cursor: pointer; }
</style>
